import { useEffect, useState } from "react";
import { Bell, BellOff, BellRing, X } from "lucide-react";
import { toast } from "sonner";
import {
  pushSupported, pushPermission, ensureSubscribed,
  requestAndSubscribe, unsubscribe, sendTest,
} from "@/lib/push";

const SNOOZE_KEY = "fl_notif_card_snoozed_at";
const SNOOZE_MS = 1000 * 60 * 60 * 24 * 3; // 3 days

function snoozed() {
  const v = Number(localStorage.getItem(SNOOZE_KEY) || 0);
  return v && Date.now() - v < SNOOZE_MS;
}

// Compact card asking field workers to enable push notifications.
// Disappears once permission is granted (handled by parent re-render).
export function EnableNotificationsCard({ compact = false }) {
  const [perm, setPerm] = useState(() => pushPermission());
  const [busy, setBusy] = useState(false);
  const [hidden, setHidden] = useState(() => snoozed());

  // If permission is already granted, silently re-subscribe (handles
  // expired subs / new devices) on mount.
  useEffect(() => {
    if (!pushSupported()) return;
    if (perm === "granted") {
      ensureSubscribed().catch(() => {});
    }
  }, [perm]);

  if (!pushSupported() || perm === "granted" || hidden) return null;

  const enable = async () => {
    setBusy(true);
    try {
      await requestAndSubscribe();
      setPerm(Notification.permission);
      toast.success("Notifications on — we'll buzz you when it matters.");
      try { await sendTest(); } catch { /* ignore */ }
    } catch (e) {
      if (Notification.permission === "denied") {
        toast.error("Notifications blocked. Enable them in your browser settings.");
      } else {
        toast.error(e?.message || "Could not enable notifications");
      }
      setPerm(Notification.permission);
    } finally {
      setBusy(false);
    }
  };

  const dismiss = () => {
    localStorage.setItem(SNOOZE_KEY, String(Date.now()));
    setHidden(true);
  };

  if (compact) {
    return (
      <button
        onClick={enable}
        disabled={busy}
        className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[#FF5F15] hover:text-[#E65100] disabled:opacity-50"
        data-testid="enable-notifications-compact"
      >
        <BellRing className="w-3.5 h-3.5" />
        {busy ? "Setting up…" : "Turn on alerts"}
      </button>
    );
  }

  return (
    <div className="rounded-[20px] bg-white dark:bg-[#101620] border border-zinc-200 dark:border-[#1F2937] p-4 flex items-start gap-3.5" data-testid="enable-notifications-card">
      <div className="w-11 h-11 rounded-xl bg-[#FF5F15] flex items-center justify-center shrink-0 shadow-lg shadow-[#FF5F15]/25">
        <Bell className="w-5 h-5 text-white" strokeWidth={2.2} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-bold uppercase tracking-tight text-[15px] text-zinc-900 dark:text-[#F8FAFC]">
          Push notifications
        </div>
        <div className="text-xs text-zinc-600 dark:text-[#94A3B8] mt-1 leading-relaxed">
          Get pinged when you&rsquo;re @mentioned in chat, a toolbox talk is required, or your dispatch changes — even if the app is closed.
        </div>
        <div className="mt-3 flex items-center gap-3">
          <button
            onClick={enable}
            disabled={busy}
            className="bg-[#FF5F15] hover:bg-[#E65100] text-white font-bold uppercase tracking-wider text-[10px] px-4 py-2.5 rounded-xl flex items-center gap-1.5 disabled:opacity-50 transition-colors"
            data-testid="enable-notifications-button"
          >
            <BellRing className="w-3.5 h-3.5" /> {busy ? "Setting up…" : "Enable alerts"}
          </button>
          <button
            onClick={dismiss}
            className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-200 px-2 py-2"
            data-testid="enable-notifications-dismiss"
          >
            Maybe later
          </button>
        </div>
      </div>
      <button onClick={dismiss} className="text-zinc-400 hover:text-zinc-900 dark:hover:text-white -mr-1" aria-label="Dismiss">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

// Settings-style toggle (used inside Profile / Settings later if needed).
export function NotificationsToggle() {
  const [perm, setPerm] = useState(() => pushPermission());
  const [busy, setBusy] = useState(false);
  if (!pushSupported()) {
    return <div className="text-xs text-zinc-500">Push notifications aren&rsquo;t supported on this device.</div>;
  }
  const on = perm === "granted";
  const toggle = async () => {
    setBusy(true);
    try {
      if (on) {
        await unsubscribe();
        toast.message("Notifications turned off for this device");
      } else {
        await requestAndSubscribe();
        toast.success("Notifications on");
      }
      setPerm(Notification.permission);
    } catch (e) {
      toast.error(e?.message || "Could not change setting");
    } finally { setBusy(false); }
  };
  return (
    <button
      onClick={toggle}
      disabled={busy}
      className={`inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider px-3 py-1.5 border ${on ? "bg-[#FF5F15] text-white border-[#FF5F15]" : "bg-white text-zinc-700 border-zinc-300"} disabled:opacity-50`}
      data-testid="notifications-toggle"
    >
      {on ? <Bell className="w-3.5 h-3.5" /> : <BellOff className="w-3.5 h-3.5" />}
      {busy ? "…" : on ? "Notifications on" : "Notifications off"}
    </button>
  );
}
